import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Input, TextArea, Badge, Card } from '../components/Components';
import { miscAPI, authAPI } from '../utils/api';
import toast from 'react-hot-toast';
import './Auth.css';

export default function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: '', email: '', password: '',
    role: searchParams.get('role') || 'candidate',
    bio: '', phone: '', location: '',
    expertise: [], coachingInterests: '', rate: 80,
    experience: '', interests: [], goals: '',
  });

  useEffect(() => {
    miscAPI.getExpertise().then((res) => setAreas(res.data.areas)).catch(() => {});
  }, []);

  const setField = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const toggleArray = (key, val) => {
    setForm((f) => ({
      ...f,
      [key]: f[key].includes(val) ? f[key].filter((x) => x !== val) : [...f[key], val],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) return toast.error('Name, email, and password are required');
    if (form.role === 'coach' && form.expertise.length === 0) return toast.error('Select at least one area of expertise');
    if (form.role === 'coach' && !form.coachingInterests) return toast.error('Coaching interests are mandatory for coaches');

    setLoading(true);
    try {
      await authAPI.sendVerification(form);
      toast.success('Verification code sent to your email!');
      navigate('/verify-email', { state: { email: form.email.toLowerCase() } });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-page__bg">
        <div className="auth-page__orb auth-page__orb--1" />
        <div className="auth-page__orb auth-page__orb--2" />
      </div>

      <Card className="auth-card auth-card--wide">
        <div className="auth-card__header">
          <Link to="/" className="auth-card__logo">
            <span className="auth-card__logo-icon">◉</span>
            <span className="auth-card__logo-text">CoachConnect</span>
          </Link>
          <h2 className="auth-card__title">Create Account</h2>
          <p className="auth-card__subtitle">Join as a coach or candidate</p>
        </div>

        <div className="auth-role-toggle">
          {['candidate', 'coach'].map((r) => (
            <button
              key={r}
              type="button"
              className={`auth-role-toggle__btn ${form.role === r ? 'auth-role-toggle__btn--active' : ''}`}
              onClick={() => setField('role', r)}
            >
              {r === 'candidate' ? '🎓 Candidate' : '🏆 Coach'}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="auth-form--scroll">
          <Input label="Full Name *" value={form.name} onChange={(e) => setField('name', e.target.value)} placeholder="John Doe" />

          <div className="auth-row">
            <Input label="Email *" type="email" value={form.email} onChange={(e) => setField('email', e.target.value)} placeholder="you@example.com" />
            <Input label="Password *" type="password" value={form.password} onChange={(e) => setField('password', e.target.value)} placeholder="Min 6 characters" />
          </div>

          <div className="auth-row">
            <Input label="Phone" value={form.phone} onChange={(e) => setField('phone', e.target.value)} placeholder="+1 234 567 890" />
            <Input label="Location" value={form.location} onChange={(e) => setField('location', e.target.value)} placeholder="City, Country" />
          </div>

          <TextArea label="Bio" value={form.bio} onChange={(e) => setField('bio', e.target.value)} placeholder="Tell us about yourself..." />

          {form.role === 'coach' && (
            <>
              <div className="input-group">
                <label className="input-label">Areas of Expertise * <span style={{ fontWeight: 400, color: '#94a3b8' }}>(select at least one)</span></label>
                <div className="auth-badges-grid">
                  {areas.map((a) => (
                    <Badge key={a} variant="primary" selected={form.expertise.includes(a)} onClick={() => toggleArray('expertise', a)}>
                      {a}
                    </Badge>
                  ))}
                </div>
              </div>

              <TextArea
                label="Coaching Interests & Motivation *"
                value={form.coachingInterests}
                onChange={(e) => setField('coachingInterests', e.target.value)}
                placeholder="Why are you passionate about coaching? What areas do you enjoy coaching in?"
              />

              <div className="auth-row">
                <Input label="Hourly Rate ($)" type="number" value={form.rate} onChange={(e) => setField('rate', Number(e.target.value))} />
                <Input label="Years of Experience" value={form.experience} onChange={(e) => setField('experience', e.target.value)} placeholder="e.g. 10 years" />
              </div>
            </>
          )}

          {form.role === 'candidate' && (
            <>
              <div className="input-group">
                <label className="input-label">Areas of Interest</label>
                <div className="auth-badges-grid">
                  {areas.map((a) => (
                    <Badge key={a} variant="primary" selected={form.interests.includes(a)} onClick={() => toggleArray('interests', a)}>
                      {a}
                    </Badge>
                  ))}
                </div>
              </div>
              <TextArea label="Goals" value={form.goals} onChange={(e) => setField('goals', e.target.value)} placeholder="What do you hope to achieve with coaching?" />
            </>
          )}

          <Button type="submit" fullWidth disabled={loading} style={{ marginTop: 8 }}>
            {loading ? 'Creating Account...' : 'Create Account →'}
          </Button>
        </form>

        <div className="auth-card__footer">
          Already have an account? <Link to="/login" className="auth-card__link">Sign In</Link>
        </div>
      </Card>
    </div>
  );
}
